import React, { useState, useRef, useEffect, useCallback } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { Search, X, Lock, ArrowRight, FileText, BookOpen, Shield, Home, Cpu, Building2, Users, GraduationCap, Phone } from 'lucide-react';
import { useNavigate } from 'react-router-dom';

// All searchable site content
const SITE_INDEX = [
  // Public pages
  { title: 'Home', path: '/', category: 'Pages', icon: Home, keywords: 'home landing platform care management', protected: false },
  { title: 'Products & Features', path: '/products', category: 'Pages', icon: FileText, keywords: 'products features care software modules overview', protected: false },
  { title: 'Pricing & Plans', path: '/pricing', category: 'Pages', icon: FileText, keywords: 'pricing plans subscription cost monthly annual', protected: false },
  { title: 'About Us', path: '/about', category: 'Pages', icon: Users, keywords: 'about company team values mission people care home', protected: false },
  { title: 'Contact Us', path: '/contact', category: 'Pages', icon: Phone, keywords: 'contact get in touch demo request support phone email', protected: false },
  { title: 'Case Studies', path: '/case-studies', category: 'Pages', icon: BookOpen, keywords: 'case studies results outcomes care providers testimonials', protected: false },
  { title: 'Partner Program', path: '/partner-program', category: 'Pages', icon: Users, keywords: 'partner vendor reseller program affiliate', protected: false },

  // Solutions
  { title: 'Domiciliary Care Software', path: '/domiciliary-care', category: 'Solutions', icon: Home, keywords: 'domiciliary homecare home visits GPS mobile carers', protected: false },
  { title: 'Care Home Management', path: '/care-home-management', category: 'Solutions', icon: Building2, keywords: 'care home residential management CQC medication eMAR rostering', protected: false },
  { title: 'Nursing Home Management', path: '/nursing-home-management', category: 'Solutions', icon: Building2, keywords: 'nursing home clinical medication NEWS2 compliance', protected: false },
  { title: 'Enterprise Solutions', path: '/enterprise-solutions', category: 'Solutions', icon: Building2, keywords: 'enterprise multi-site large scale care group organisation', protected: false },
  { title: 'AI Features', path: '/ai-features', category: 'Solutions', icon: Cpu, keywords: 'AI artificial intelligence care plans automation insights', protected: false },
  { title: 'Data Migration', path: '/data-migration', category: 'Solutions', icon: FileText, keywords: 'data migration import transfer legacy system onboarding', protected: false },
  { title: 'Customer Support', path: '/customer-support', category: 'Solutions', icon: Phone, keywords: 'customer support help desk live chat training onboarding', protected: false },

  // Training
  { title: 'Training & Resources', path: '/training', category: 'Training', icon: GraduationCap, keywords: 'training resources video tutorials documentation courses certification', protected: false },
  { title: 'Video Tutorials', path: '/training#videos', category: 'Training', icon: GraduationCap, keywords: 'video tutorials watch learn how-to guides', protected: false },
  { title: 'Documentation', path: '/training#docs', category: 'Training', icon: FileText, keywords: 'documentation docs user guide reference manual', protected: false },
  { title: 'Knowledge Base', path: '/training#kb', category: 'Training', icon: BookOpen, keywords: 'knowledge base articles FAQs help support articles', protected: false },
  { title: 'Certification Courses', path: '/training#courses', category: 'Training', icon: GraduationCap, keywords: 'certification courses certified training badges compliance mandatory', protected: false },
  { title: 'Training Portal', path: '/training-portal', category: 'Training', icon: Lock, keywords: 'training portal login courses modules LMS staff learning', protected: true },
  { title: 'Manage Training', path: '/manage-training', category: 'Training', icon: Lock, keywords: 'manage training admin courses assign staff compliance dashboard', protected: true },
  { title: 'Training Configuration', path: '/training-configuration', category: 'Training', icon: Lock, keywords: 'training configuration settings certificates themes admin', protected: true },

  // Legal
  { title: 'Privacy Policy', path: '/privacy-policy', category: 'Legal', icon: Shield, keywords: 'privacy policy data protection GDPR personal information', protected: false },
  { title: 'Terms of Service', path: '/terms-of-service', category: 'Legal', icon: FileText, keywords: 'terms of service conditions agreement usage licence', protected: false },
  { title: 'Cookie Policy', path: '/cookie-policy', category: 'Legal', icon: FileText, keywords: 'cookie policy cookies tracking analytics preferences', protected: false },
  { title: 'GDPR Compliance', path: '/gdpr', category: 'Legal', icon: Shield, keywords: 'GDPR data rights UK compliance ICO data controller processor', protected: false },
  { title: 'Data Processing Agreement', path: '/data-processing', category: 'Legal', icon: FileText, keywords: 'data processing agreement DPA sub-processors Article 28', protected: false },
];

const PAGE_SIZE = 5;

// Build a short snippet from keywords, highlighting the matched word
function Highlight({ text, query }) {
  if (!query.trim()) return <span>{text}</span>;
  const regex = new RegExp(`(${query.trim().replace(/[.*+?^${}()|[\]\\]/g, '\\$&')})`, 'gi');
  const parts = text.split(regex);
  return (
    <span>
      {parts.map((part, i) =>
        regex.test(part) ? (
          <mark key={i} className="bg-primary/20 text-primary rounded px-0.5">{part}</mark>
        ) : (
          <span key={i}>{part}</span>
        )
      )}
    </span>
  );
}

function getSnippet(item, query) {
  // Try to find the matching word in keywords and build a short phrase
  const q = query.trim().toLowerCase();
  const words = item.keywords.split(' ');
  const matchIdx = words.findIndex((w) => w.toLowerCase().includes(q));
  if (matchIdx === -1) return item.category;
  const start = Math.max(0, matchIdx - 2);
  const end = Math.min(words.length, matchIdx + 4);
  return words.slice(start, end).join(' ') + (end < words.length ? '…' : '');
}

export default function SiteSearch() {
  const [open, setOpen] = useState(false);
  const [query, setQuery] = useState('');
  const [visibleCount, setVisibleCount] = useState(PAGE_SIZE);
  const inputRef = useRef(null);
  const containerRef = useRef(null);
  const navigate = useNavigate();

  const results = query.trim().length >= 1
    ? SITE_INDEX.filter((item) => {
        const q = query.toLowerCase();
        return item.title.toLowerCase().includes(q) || item.keywords.toLowerCase().includes(q) || item.category.toLowerCase().includes(q);
      })
    : [];

  const visibleResults = results.slice(0, visibleCount);
  const hasMore = results.length > visibleCount;

  const openSearch = () => {
    setOpen(true);
    setTimeout(() => inputRef.current?.focus(), 80);
  };

  const closeSearch = useCallback(() => {
    setOpen(false);
    setQuery('');
    setVisibleCount(PAGE_SIZE);
  }, []);

  const handleSelect = (item) => {
    closeSearch();
    if (item.path.includes('#')) {
      const [path, hash] = item.path.split('#');
      navigate(path);
      setTimeout(() => {
        const el = document.getElementById(hash);
        if (el) el.scrollIntoView({ behavior: 'smooth' });
      }, 300);
    } else {
      navigate(item.path);
    }
  };

  useEffect(() => {
    const handleKey = (e) => {
      if ((e.metaKey || e.ctrlKey) && e.key === 'k') { e.preventDefault(); open ? closeSearch() : openSearch(); }
      if (e.key === 'Escape') closeSearch();
    };
    window.addEventListener('keydown', handleKey);
    return () => window.removeEventListener('keydown', handleKey);
  }, [open, closeSearch]);

  useEffect(() => {
    setVisibleCount(PAGE_SIZE);
  }, [query]);

  // Group results by category
  const grouped = visibleResults.reduce((acc, item) => {
    if (!acc[item.category]) acc[item.category] = [];
    acc[item.category].push(item);
    return acc;
  }, {});

  const categoryOrder = ['Pages', 'Solutions', 'Training', 'Legal'];

  return (
    <>
      {/* Search trigger button */}
      <button
        onClick={openSearch}
        className="flex items-center gap-1.5 px-2.5 py-1.5 rounded-lg hover:bg-muted/50 text-muted-foreground hover:text-foreground transition-all group"
        aria-label="Search site"
      >
        <Search className="w-4 h-4 group-hover:scale-110 transition-transform" />
      </button>

      {/* Search overlay */}
      <AnimatePresence>
        {open && (
          <>
            <motion.div
              initial={{ opacity: 0 }}
              animate={{ opacity: 1 }}
              exit={{ opacity: 0 }}
              className="fixed inset-0 z-[9990] bg-black/50 backdrop-blur-sm"
              onClick={closeSearch}
            />
            <motion.div
              initial={{ opacity: 0, scale: 0.95, y: -16 }}
              animate={{ opacity: 1, scale: 1, y: 0 }}
              exit={{ opacity: 0, scale: 0.95, y: -16 }}
              transition={{ duration: 0.18 }}
              className="fixed top-20 left-4 right-4 sm:left-1/2 sm:right-auto sm:-translate-x-1/2 z-[9991] w-auto sm:w-[580px] sm:max-w-[calc(100vw-32px)]"
            >
              <div className="bg-card border border-border/50 rounded-2xl shadow-2xl shadow-black/20 overflow-hidden">
                {/* Search input */}
                <div className="flex items-center gap-3 px-4 py-3 border-b border-border/30">
                  <Search className="w-5 h-5 text-primary flex-shrink-0" />
                  <input
                    ref={inputRef}
                    type="text"
                    value={query}
                    onChange={(e) => setQuery(e.target.value)}
                    placeholder="Search pages, features, training, legal…"
                    className="flex-1 bg-transparent text-foreground placeholder:text-muted-foreground text-sm outline-none"
                  />
                  {query && (
                    <button onClick={() => setQuery('')} className="text-muted-foreground hover:text-foreground transition-colors">
                      <X className="w-4 h-4" />
                    </button>
                  )}
                  <button onClick={closeSearch} className="text-xs text-muted-foreground hover:text-foreground border border-border/40 rounded px-1.5 py-0.5 font-mono">
                    Esc
                  </button>
                </div>

                {/* Results */}
                <div className="max-h-[60vh] overflow-y-auto">
                  {query.trim() === '' && (
                    <div className="px-4 py-8 text-center text-muted-foreground">
                      <Search className="w-8 h-8 mx-auto mb-2 opacity-30" />
                      <p className="text-sm">Start typing to search pages, features, training & more</p>
                    </div>
                  )}

                  {query.trim().length >= 1 && results.length === 0 && (
                    <div className="px-4 py-8 text-center text-muted-foreground">
                      <p className="text-sm font-medium">No results for <span className="text-foreground">"{query}"</span></p>
                      <p className="text-xs mt-1">Try different keywords</p>
                    </div>
                  )}

                  {results.length > 0 && (
                    <div className="p-2">
                      {categoryOrder.map((cat) => {
                        const items = grouped[cat];
                        if (!items || items.length === 0) return null;
                        return (
                          <div key={cat} className="mb-3">
                            <p className="text-[10px] font-bold text-muted-foreground uppercase tracking-widest px-3 py-1.5">{cat}</p>
                            {items.map((item) => {
                              const ItemIcon = item.icon;
                              return (
                                <button
                                  key={item.path}
                                  onClick={() => handleSelect(item)}
                                  className="w-full flex items-center gap-3 px-3 py-2.5 rounded-xl hover:bg-muted/60 transition-colors text-left group"
                                >
                                  <div className={`w-8 h-8 rounded-lg flex items-center justify-center flex-shrink-0 ${item.protected ? 'bg-amber-50 border border-amber-200' : 'bg-primary/8 border border-border/30'}`}>
                                    <ItemIcon className={`w-4 h-4 ${item.protected ? 'text-amber-500' : 'text-primary'}`} />
                                  </div>
                                  <div className="flex-1 min-w-0">
                                    <div className="flex items-center gap-2">
                                      <span className="text-sm font-medium text-foreground truncate">
                                        <Highlight text={item.title} query={query} />
                                      </span>
                                      {item.protected && (
                                        <span className="flex items-center gap-1 text-[10px] font-semibold text-amber-600 bg-amber-50 border border-amber-200 px-1.5 py-0.5 rounded-full flex-shrink-0">
                                          <Lock className="w-2.5 h-2.5" /> Protected
                                        </span>
                                      )}
                                    </div>
                                    <p className="text-xs text-muted-foreground truncate">
                                      <Highlight text={getSnippet(item, query)} query={query} />
                                    </p>
                                  </div>
                                  <ArrowRight className="w-3.5 h-3.5 text-muted-foreground/40 group-hover:text-primary group-hover:translate-x-0.5 transition-all flex-shrink-0" />
                                </button>
                              );
                            })}
                          </div>
                        );
                      })}

                      {/* Load more */}
                      {hasMore && (
                        <div className="px-3 pt-1 pb-2">
                          <button
                            onClick={() => setVisibleCount((c) => c + PAGE_SIZE)}
                            className="w-full py-2 text-xs font-medium text-primary hover:text-primary/80 border border-primary/20 hover:border-primary/40 rounded-xl bg-primary/5 hover:bg-primary/10 transition-all"
                          >
                            Load more results ({results.length - visibleCount} remaining)
                          </button>
                        </div>
                      )}
                    </div>
                  )}
                </div>

                {/* Footer hint */}
                <div className="px-4 py-2.5 border-t border-border/20 bg-muted/10 flex items-center justify-between">
                  <p className="text-[10px] text-muted-foreground">
                    {results.length > 0 ? `${results.length} result${results.length !== 1 ? 's' : ''}` : 'Search across all pages & features'}
                  </p>
                  <div className="flex items-center gap-2 text-[10px] text-muted-foreground">
                    <span className="flex items-center gap-1"><Lock className="w-2.5 h-2.5 text-amber-500" /> = login required</span>
                  </div>
                </div>
              </div>
            </motion.div>
          </>
        )}
      </AnimatePresence>
    </>
  );
}